import React, { useState, useEffect, useMemo } from "react";
import EventModal from "../components/Modal/event-modal";
// import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import { api, s3Url } from "../service/api.service";

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function Nirnay() {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [calendarData, setCalendarData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState(null);

  const today = new Date();

  //----------------------------------------
  // API
  //----------------------------------------

  useEffect(() => {
    fetchCalendar();
  }, [currentMonth]);

  const fetchCalendar = async () => {
    try {
      setLoading(true);

      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();

      const response = await fetch(
        `${api.defaults.baseURL}nirnay/v2/calendar-data?month=${month}&year=${year}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      const json = await response.json();

      setCalendarData(json.data.calendarData || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  //----------------------------------------
  // Create lookup map
  //----------------------------------------

  const eventMap = useMemo(() => {
    const map = {};

    calendarData.forEach((item) => {
      const date = new Date(item.start);

      const key = date.toISOString().split("T")[0];

      map[key] = item;
    });

    return map;
  }, [calendarData]);

  //----------------------------------------
  // Calendar Grid
  //----------------------------------------

  const days = useMemo(() => {
    const year = currentMonth.getFullYear();

    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);

    const lastDay = new Date(year, month + 1, 0);

    const startWeekDay = firstDay.getDay();

    const totalDays = lastDay.getDate();

    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const cells = [];

    //----------------------------------------
    // Previous Month
    //----------------------------------------

    for (let i = startWeekDay - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;

      const date = new Date(year, month - 1, day);

      cells.push({
        date,
        number: day,
        current: false,
      });
    }

    //----------------------------------------
    // Current Month
    //----------------------------------------

    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);

      const key = date.toISOString().split("T")[0];

      cells.push({
        date,
        number: day,
        current: true,
        event: eventMap[key],
      });
    }

    //----------------------------------------
    // Next Month
    //----------------------------------------

    while (cells.length % 7 !== 0) {
      const day = cells.length - (startWeekDay + totalDays) + 1;

      const date = new Date(year, month + 1, day);

      cells.push({
        date,
        number: day,
        current: false,
      });
    }

    return cells;
  }, [currentMonth, eventMap]);

  //----------------------------------------
  // Navigation
  //----------------------------------------

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  //----------------------------------------
  // Helpers
  //----------------------------------------

  const isToday = (date) => {
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  //----------------------------------------
  // Header Data
  //----------------------------------------

  const todayKey = today.toISOString().split("T")[0];

  const todayEvent = eventMap[todayKey];

  //----------------------------------------
  // UI
  //----------------------------------------

  const getImageUrl = (image) => {
    if (!image?.imageUrl) return null;
    return `${s3Url}${image.imageUrl}`;
  };

  return (
    <>
      {/* <Header /> */}

      <section className="bg-[#fff8f4] min-h-screen py-28 md:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4">
          {/* PAGE TITLE */}

          <h1 className="text-center text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 lg:mb-10">
            Nirnay
          </h1>

          {/* TOP HEADER */}

          <div className="bg-white rounded-3xl shadow-sm border">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {/* TODAY */}
              <div className="p-5 sm:p-6 lg:p-8 text-center lg:border-r border-b lg:border-b-0">
                <h3 className="text-red-700 text-3xl font-bold">Today</h3>
                <p className="mt-3 text-2xl font-semibold">{today.getDate()}</p>
                <p>{monthNames[today.getMonth()]}</p>
                <p>{today.getFullYear()}</p>
              </div>
              {/* GUJARATI INFO */}
              <div className="p-5 sm:p-6 lg:p-8 text-center lg:border-r border-b lg:border-b-0">
                <h3 className="text-red-700 text-3xl font-bold">
                  Samvat {todayEvent?.extendedProps?.samvat}
                </h3>
                <p className="mt-3">{todayEvent?.extendedProps?.paksh}</p>
                <p>{todayEvent?.extendedProps?.tithi}</p>
                <p>{todayEvent?.extendedProps?.nakshatra}</p>
              </div>
              {/* SUN */}
              <div className="p-5 sm:p-6 lg:p-8 text-center">
                <div className="space-y-4">
                  <div>🌅 Sunrise : 6:08</div>
                  <div>🌇 Sunset : 7:32</div>
                </div>
              </div>
            </div>
            {/* MONTH NAVIGATION */}
            <div className="border-t p-4 sm:p-6 flex flex-wrap justify-center items-center gap-4 sm:gap-8">
              <button
                onClick={previousMonth}
                className="w-10 h-10 rounded-full border hover:bg-red-600 hover:text-white"
              >
                ←
              </button>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-center">
                {monthNames[currentMonth.getMonth()]}{" "}
                {currentMonth.getFullYear()}
              </h2>
              <button
                onClick={nextMonth}
                className="w-10 h-10 rounded-full border hover:bg-red-600 hover:text-white"
              >
                →
              </button>
            </div>
          </div>

          {/* WEEK DAYS & CALENDAR SCROLLABLE WRAPPER */}
          <div className="mt-8 overflow-x-auto">
            <div className="min-w-[980px]">
              {/* WEEK DAYS */}
              <div className="grid grid-cols-7 rounded-t-xl overflow-hidden">
                {weekDays.map((day) => (
                  <div
                    key={day}
                    className="bg-red-700 text-white py-3 lg:py-5 text-center text-sm sm:text-base lg:text-xl font-semibold"
                  >
                    {day}
                  </div>
                ))}
              </div>
              {/* CALENDAR */}
              {loading ? (
                <div className="text-center py-20">Loading...</div>
              ) : (
                <div className="grid grid-cols-7 overflow-hidden rounded-b-2xl border border-red-200 bg-white shadow-lg min-w-[980px]">
                  {days.map((day, index) => {
                    const event = day.event;
                    return (
                      <div
                        key={index}
                        className={`relative min-h-[150px] lg:min-h-[170px] border border-red-100 p-2 sm:p-3 lg:p-4 transition-all duration-200 hover:bg-red-50 hover:shadow-inner cursor-pointer ${
                          day.current ? "bg-white" : "bg-red-50"
                        }`}
                        onClick={() => {
                          if (day.event) {
                            setSelectedEvent(day.event);
                          }
                        }}
                      >
                        <div
                          className={`text-2xl sm:text-3xl lg:text-[32px] leading-none font-semibold ${
                            day.current ? "text-[#5b2a1d]" : "text-gray-400"
                          }`}
                        >
                          {day.number}
                        </div>
                        {isToday(day.date) && (
                          <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-red-500"></div>
                        )}
                        {event && (
                          <>
                            <div className="mt-5 text-red-600 text-sm font-semibold line-clamp-1">
                              {event.extendedProps.paksh}{" "}
                              {event.extendedProps.tithi}
                            </div>
                            <div className="mt-1 text-xs text-neutral-700 leading-5 line-clamp-3">
                              {event.extendedProps.description}
                            </div>
                            {event.extendedProps.imageId?.imageUrl && (
                              <img
                                src={getImageUrl(event.extendedProps.imageId)}
                                alt={event.extendedProps.imageId.title}
                                className="absolute top-4 right-[2.5rem] w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 object-contain"
                                loading="lazy"
                              />
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <EventModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
      <Footer />
    </>
  );
}
